// Sidebar sections — the display layer over buildRoutes() (client/src/routes.jsx).
//
// The registry (modules.config.js → /api/meta/modules) owns WHAT the user may see and
// the RBAC gate owns WHETHER they see it; this file only owns WHERE it sits in the
// sidebar. Nothing here creates a module or a permission, so restructuring the nav
// never needs rbac:setup or new grants — DefaultLayout filters by can() first and
// sections an already-allowed list, dropping sections left with nothing permitted.
//
// Resolution is path-first, module-second: most routes inherit their module's section,
// but core splits ('/' → Main, '/api-token' → Developer) and the hand-written pages
// (Chat, Voice Settings, Connectors) pin explicitly. Unknown paths fall back to Main
// so a future route is always visible somewhere rather than silently nowhere.

export const SECTIONS = [
  { key: 'main', label: 'Main', icon: 'layout-dashboard' },
  { key: 'ai', label: 'AI & Voice', icon: 'bot' },
  { key: 'data', label: 'Data & Integrations', icon: 'database' },
  { key: 'tools', label: 'Tools & Automation', icon: 'wrench' },
  { key: 'dev', label: 'Developer', icon: 'code' },
  { key: 'admin', label: 'Administration', icon: 'shield' },
];

const SECTION_BY_PATH = {
  '/': 'main',
  '/chat': 'main',
  '/app-company': 'main',
  '/app-application': 'main',
  '/voice-settings': 'ai',
  '/connectors': 'data',
  '/api-token': 'dev',
};

const SECTION_BY_MODULE = {
  core: 'main',
  companies: 'main',
  applications: 'main',
  agents: 'ai',
  calls: 'ai',
  knowledge: 'data',
  connections: 'data',
  toolproviders: 'data',
  connectors: 'data',
  tools: 'tools',
  workflows: 'tools',
  uiaudits: 'tools',
  embed: 'dev',
  mockapi: 'dev',
  browser: 'dev',
  users: 'admin',
  roles: 'admin',
  settings: 'admin',
};

export function sectionForRoute(route = {}) {
  return SECTION_BY_PATH[route.path] || SECTION_BY_MODULE[route.module] || 'main';
}

/** Allowed routes → sections in SECTION_ORDER, empties dropped. Order within a
 *  section is the caller's (buildRoutes registry order), never re-sorted here. */
export function groupIntoSections(routes = []) {
  const buckets = new Map(SECTIONS.map((s) => [s.key, []]));
  for (const route of routes) {
    const key = sectionForRoute(route);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(route);
  }
  return SECTIONS.map((s) => ({ ...s, routes: buckets.get(s.key) || [] })).filter(
    (s) => s.routes.length > 0,
  );
}
